import {
  Root as AvatarRoot,
  Image as AvatarImage,
  Fallback as AvatarFallback,
} from '@radix-ui/react-avatar';
import Image from 'next/image';
import type React from 'react';
import { type CSSProperties, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { tv } from 'tailwind-variants';

import { AvatarSkeleton } from '@/components/Skeleton';

import { classNames } from '@/utils/classnames';

export type AvatarStatus = 'idle' | 'loading' | 'loaded' | 'error';
export type AvatarRadius = CSSProperties['borderRadius'];

export type AvatarProps = Omit<ComponentPropsWithoutRef<'img'>, 'width' | 'height' | 'src'> & {
  /**
   * URL of the image to display.
   */
  src?: string;
  /**
   * URL of the fallback image to be displayed on image load error.
   */
  fallbackSrc?: string;
  /**
   * Border radius of the avatar.
   * @default '100%'
   */
  radius?: AvatarRadius;
  /**
   * The size of the avatar.
   * @default 24
   */
  size?: number;
  /**
   * Fallback content displayed when the image fails to load and fallbackSrc property is not defined.
   */
  children?: ReactNode;
};

export const avatarStyles = tv({
  slots: {
    root: [
      'inline-block relative shrink-0 overflow-hidden shadow-15 rounded-[var(--avatar-border-radius)]',
      'after:size-full after:absolute after:top-0 after:left-0 after:shadow-inner-10 after:border-[0.5px]',
      'after:border-special-border-avatar after:rounded-[var(--avatar-border-radius)]',
    ],
    fallback: undefined,
  },
  variants: {
    loading: {
      true: { root: 'after:hidden', fallback: 'flex items-center justify-center' },
    },
  },
});

export const Avatar = (props: AvatarProps) => {
  const {
    alt = '',
    className,
    children,
    size = 24,
    src = '',
    fallbackSrc,
    radius = '100%',
    style,
    ...imageProps
  } = props;

  const [status, setStatus] = useState<AvatarStatus>('loading');
  const { root, fallback } = avatarStyles({ loading: status === 'loading' });

  return (
    <AvatarRoot
      className={classNames(root(), className)}
      style={
        {
          ...style,
          'width': size,
          'height': size,
          '--avatar-border-radius': radius,
        } as CSSProperties
      }
    >
      <AvatarImage src={src} onLoadingStatusChange={setStatus} asChild={true}>
        <Image
          alt={alt}
          src={src}
          width={size}
          height={size}
          style={{ width: size, height: size, objectFit: 'cover' }}
          {...imageProps}
        />
      </AvatarImage>
      <AvatarFallback className={fallback()}>
        {status === 'loading' && <AvatarSkeleton size={size} radius={radius} />}
        {status === 'error' && fallbackSrc != null && (
          <Avatar src={fallbackSrc} size={size} radius={radius} />
        )}
        {status === 'error' && children != null && children}
      </AvatarFallback>
    </AvatarRoot>
  );
};
